import { isValidInsuranceRegion } from 'lib/insurance/insuranceCountries'
import { isBeyondCfmrLeadTime } from 'lib/insurance/insuranceHelpers'
import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import isBookingProtectionEnabled from './isBookingProtectionEnabled'
import hasBookingProtectionInsuranceFetchError from './hasBookingProtectionInsuranceFetchError'
import getInsuranceDestinationCountriesFromCart from '../view/getInsuranceDestinationCountriesFromCart'
import getInsuranceDestinationCountriesFromExistingOrder from '../view/getInsuranceDestinationCountriesFromExistingOrder'
import { getInsuranceDatesFromExistingOrder } from './insurance'
import { getOccupantsFromCartItems } from './getCheckoutInsuranceTravellers'
import getInsuranceDatesFromCart from './getInsuranceDatesFromCart'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { TRAVEL_PROTECTION_VALID_COUNTRIES } from 'constants/config/region'
import moment from 'moment'
import { checkoutHasPreselectedUpstreamInsurance, getInsuranceEnabledCheckoutItems } from '../view/insurance'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'

const validateInsuranceDates = (startDate: string, regionCode: string): boolean => {
  if (TRAVEL_PROTECTION_VALID_COUNTRIES.includes(regionCode)) return true
  return moment(startDate).diff(moment(), 'days') <= 365
}
/**
 * Get the insurance parameters based on the checkout cart items
 *
 * @remarks
 * - Currently only supports "LE Hotels" (instant booking), "Bedbank" and "TourV2"
 */
const isInsuranceSupportedForCartItems = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getInsuranceEnabledCheckoutItems(state),
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => getInsuranceDestinationCountriesFromCart(state),
  (state: App.State) => getOccupantsFromCartItems(state),
  (regionCode, postPurchase, items, dateRange, destinationCountries, occupants): boolean => {
    return !!(
      occupants &&
      items.length &&
      dateRange &&
      destinationCountries.length &&
      (!postPurchase || postPurchase === 'select-date') &&
      validateInsuranceDates(dateRange.startDate, regionCode)
    )
  },
)

const isInsuranceSupportedForExistingOrder = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getInsuranceDatesFromExistingOrder(state),
  (state: App.State) => getInsuranceDestinationCountriesFromExistingOrder(state),
  (regionCode, postPurchase, insuranceDates, destCountries): boolean => {
    return !!(
      postPurchase == 'insurance' &&
      insuranceDates &&
      destCountries.length &&
      validateInsuranceDates(insuranceDates.startDate, regionCode)
    )
  },
)

/**
 * Whether XCover travel insurance can be offered for this checkout, before applying the
 * booking-protection (CFMR) exclusivity rule. When CFMR is available to a customer, the regular
 * insurance section yields to it (see `isInsuranceSupported`); the side-by-side insurance
 * experiment uses this selector to present both products together instead.
 */
export const isInsuranceSupportedAlongsideBookingProtection = createSelector(
  (state: App.State) => state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInsuranceXCoverEnabled],
  (state: App.State) => isInsuranceSupportedForCartItems(state),
  (state: App.State) => isInsuranceSupportedForExistingOrder(state),
  (state: App.State) => state.geo.currentRegionCode,
  (
    isFeatureFlagEnabled,
    isInsuranceAvailableForCartItems,
    isInsuranceAvailableForExistingOrder,
    currentRegionCode,
  ): boolean => {
    if (!isFeatureFlagEnabled) return false
    if (!isValidInsuranceRegion(currentRegionCode)) return false
    if (!isInsuranceAvailableForCartItems && !isInsuranceAvailableForExistingOrder) return false

    return true
  },
)

/**
 * Whether XCover is offered in place of CFMR because the trip starts more than `CFMR_MAX_LEAD_TIME_DAYS`
 * after today. It only applies where XCover can be offered for the cart, so carts and regions without an
 * XCover product keep CFMR. Spoofed agents keep both products, protection chosen upstream (a Protect fare or
 * a CFMR flex package) keeps CFMR for the rest of the booking, and post-purchase checkouts apply the rule
 * through `canAddInsuranceBookingProtectionToOrder`.
 */
export const shouldOfferInsuranceInsteadOfCfmr = createSelector(
  (state: App.State) => isInsuranceSupportedAlongsideBookingProtection(state),
  (state: App.State) => getInsuranceDatesFromCart(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => !!findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => checkoutHasPreselectedUpstreamInsurance(state),
  (isInsuranceAvailable, dates, isSpoofedUser, isPostPurchase, hasPreselectedUpstreamInsurance): boolean => {
    if (isSpoofedUser || isPostPurchase || hasPreselectedUpstreamInsurance) return false

    return isInsuranceAvailable && isBeyondCfmrLeadTime(dates?.startDate)
  },
)

const isInsuranceSupported = createSelector(
  (state: App.State) => isInsuranceSupportedAlongsideBookingProtection(state),
  (state: App.State) => isBookingProtectionEnabled(state),
  (state: App.State) => hasBookingProtectionInsuranceFetchError(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => shouldOfferInsuranceInsteadOfCfmr(state),
  (
    isSupportedAlongsideBookingProtection,
    isBookingProtectionEnabled,
    bookingProtectionFetchFailed,
    isSpoofed,
    offerInsuranceInsteadOfCfmr,
  ): boolean => {
    if (!isSupportedAlongsideBookingProtection) return false
    // CFMR takes priority over XCover while it is enabled and loading, except on trips that start too far
    // ahead for it. Once its product list or first quote has failed, XCover is offered instead wherever the
    // region supports it.
    if (isBookingProtectionEnabled && !bookingProtectionFetchFailed && !isSpoofed && !offerInsuranceInsteadOfCfmr) return false

    return true
  },
)

export default isInsuranceSupported
